import React, { useCallback, useEffect, useState } from 'react';

const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' };
const textareaStyle = { ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' };
const btnStyle = { padding: '8px 14px', fontSize: 14, border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' };
const btnPrimary = { ...btnStyle, background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' };

const STAGE_LABELS = {
  organizer_pending: '審核中',
  organizer_rejected: '退回',
  organizer_approved: '通過',
  organizer_cancelled: '取消',
  organizer_day_before: '活動前一天',
  organizer_pass_granted: '日券發放',
};

const VARS = [
  '{{活動名稱}}', '{{活動日期}}', '{{活動時段}}', '{{場地}}',
  '{{主辦名稱}}', '{{退回原因}}', '{{核准備註}}', '{{日券數量}}', '{{日券動作}}',
  '{{店名}}', '{{是否修訂}}', '{{活動連結}}',
];

export default function OrganizerReviewNotifySection({ adminAuthHeaders, formSectionStyle }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [profileId, setProfileId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeStage, setActiveStage] = useState('organizer_pending');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/vb/admin/organizer-review-notify', { headers: adminAuthHeaders() });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch {
        throw new Error(res.status === 404 ? 'API 尚未部署（請重啟後端）' : `載入失敗（HTTP ${res.status}）`);
      }
      if (!data?.ok) throw new Error(data?.error || '載入失敗');
      setProfileId(data.data?.profileId ?? null);
      const list = data.data?.templates || [];
      setTemplates(list);
      if (list[0]?.stage) setActiveStage(list[0].stage);
    } catch (e) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [adminAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const cur = templates.find((t) => t.stage === activeStage) || {
    stage: activeStage,
    isEnabled: true,
    lineText: '',
    emailSubject: '',
    emailBody: '',
  };

  const patchCur = (patch) => {
    setTemplates((prev) => {
      const exists = prev.some((t) => t.stage === activeStage);
      if (!exists) return [...prev, { ...cur, ...patch, stage: activeStage }];
      return prev.map((t) => (t.stage === activeStage ? { ...t, ...patch } : t));
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      const res = await fetch('/api/vb/admin/organizer-review-notify', {
        method: 'PUT',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ profileId, templates }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '儲存失敗');
      setProfileId(data.data?.profileId ?? profileId);
      setTemplates(data.data?.templates || templates);
      setOkMsg('已儲存');
    } catch (e) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={formSectionStyle || { padding: 16, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>主辦審核通知（LINE 群組）</h2>
      <p style={{ fontSize: 13, color: '#666', marginTop: 0 }}>
        三階段推播至主辦帳號綁定的 LINE 群組。管理員 Email 送審通知維持既有流程。
      </p>
      {error ? <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, marginBottom: 12 }}>{error}</div> : null}
      {okMsg ? <div style={{ padding: 10, background: '#ecfdf5', color: '#166534', borderRadius: 8, marginBottom: 12 }}>{okMsg}</div> : null}
      {loading ? (
        <p style={{ color: '#666' }}>載入中…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.keys(STAGE_LABELS).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setActiveStage(s)}
                style={{
                  ...btnStyle,
                  textAlign: 'left',
                  background: activeStage === s ? '#7b4f2f' : '#fff',
                  color: activeStage === s ? '#fff' : '#333',
                  borderColor: activeStage === s ? '#7b4f2f' : '#ccc',
                }}
              >
                {STAGE_LABELS[s]}
              </button>
            ))}
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={!!cur.isEnabled}
                onChange={(e) => patchCur({ isEnabled: e.target.checked })}
              />
              啟用此階段 LINE 通知
            </label>
            {activeStage === 'organizer_day_before' ? (
              <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>活動前一天 10:00 自動送出；內容空白則不送</div>
            ) : null}
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>LINE 文字</label>
            <textarea
              style={{ ...textareaStyle, marginBottom: 10 }}
              value={cur.lineText || ''}
              onChange={(e) => patchCur({ lineText: e.target.value })}
              placeholder="推播至群組的文字內容"
            />
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Email 主旨（選填，另寄主辦 Email 時用）</label>
            <input
              style={{ ...inputStyle, marginBottom: 10 }}
              value={cur.emailSubject || ''}
              onChange={(e) => patchCur({ emailSubject: e.target.value })}
            />
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Email 內文 HTML（選填）</label>
            <textarea
              style={{ ...textareaStyle, marginBottom: 10 }}
              value={cur.emailBody || ''}
              onChange={(e) => patchCur({ emailBody: e.target.value })}
            />
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              可用變數：{VARS.join(' ')}
            </div>
            <button type="button" style={btnPrimary} onClick={save} disabled={saving}>
              {saving ? '儲存中…' : '儲存模板'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
